import { drawContract } from '@daymade/api-contracts';
import { Monthly10k } from '@daymade/common-lib/constants';
import { ClientInferResponseBody } from '@ts-rest/core';
import AboutUsModule from 'components/about-us/AboutUsModule';
import AboutUsSiteContainer from 'components/about-us/AboutUsSiteContainer';
import FaqCard from 'components/FaqCard';
import MainLayout from 'components/layout/MainLayout';
import { DesktopPrizeGallery } from 'components/monthly-giveaway/DesktopPrizeGallery';
import Monthly10kGiveawayIcon from 'components/monthly-giveaway/Monthly10kGiveawayIcon.svg';
import { PeekCarousel } from 'components/monthly-giveaway/PeekCarousel';
import { CurrentUserProgressionBar } from 'components/monthly-giveaway/ProgressionBar';
import {
  getSectionHeaderIcon,
  renderBundleItems,
} from 'components/prizes/prize-page/HeaderSectionPrize';
import { ReadMoreSection } from 'components/ReadMoreSection';
import { useUserStore } from 'hooks/stores/useUserStore';
import { observer } from 'mobx-react-lite';
import { GetServerSideProps } from 'next';
import { NextSeo } from 'next-seo';
import { useRouter } from 'next/router';
import { useEffect } from 'react';
import { drawApiClient } from 'utils/apiClient';
import { ASSET_PREFIX } from 'utils/constants';
import { formatDrawDate, getMonthName } from 'utils/draw/getMonthName';
import HttpStatusCode from 'utils/HttpStatusCode';
import { parsePrizeHtmlSections } from 'utils/ParseHtml';
import { RoundTripSerialized } from 'utils/TypeUtils';
import styles from './monthly-10k-giveaway.module.css';
import { toMidSentenceCase } from '@daymade/common-lib/formatting';
import { convertNewLinesToBr } from 'utils/rendering/render';
import Link from 'next/link';

const imageLocation = `${ASSET_PREFIX}/images/monthly-10k-giveaway`;

const LAST_MONTH_WINNER_SECTION_TITLE = "Last month's\nwinner";

const alternativePrizeMessagingDates: [startDate: string, endDate: string][] = [
  // April's draw starts at end of March which is in BST
  ['2026-03-31T21:00:00+01:00', '2026-04-30T21:00:00+01:00'],
  ['2026-04-30T21:00:00+01:00', '2026-05-31T21:00:00+01:00'],
];

function shouldUseDifferentAlternativePrizeMessaging() {
  const now = new Date();
  return alternativePrizeMessagingDates.some((range) => {
    return now >= new Date(range[0]) && now < new Date(range[1]);
  });
}

const getGoodToKnowCards = (drawDate: Date | string | null | undefined) => [
  {
    title: 'Weekly Entry',
    body: 'Subscribers unlock entries every week',
    iconSvg: '🎟️',
  },
  {
    title: '1 winner',
    body: '1 guaranteed winner monthly',
    iconSvg: '🥳',
  },
  {
    title: 'Draw date',
    body: formatDrawDate(drawDate),
    iconSvg: '🗓️',
  },
  {
    title: 'Alternative prizes',
    mobileTitle: 'Alt. Prizes',
    body: shouldUseDifferentAlternativePrizeMessaging() ? (
      <>
        You can also opt for this{' '}
        <Link href="/prizes/details/maldives">
          <a>£10k Maldives trip</a>
        </Link>{' '}
        for 2
      </>
    ) : (
      'A choice of other £10k prizes available'
    ),
    iconSvg: '🎁',
  },
];

type TDrawResponse = ClientInferResponseBody<
  typeof drawContract.public.getDrawTypeCached,
  200
>;

type TLastRaffleResponse = ClientInferResponseBody<
  typeof drawContract.public.getLastRafflePrizeAndWinner,
  200
>;

type Monthly10kGiveawayProps = {
  draw: TDrawResponse;
  lastMonthInfo?: {
    prize: NonNullable<TLastRaffleResponse>['prize'];
    winner: NonNullable<TLastRaffleResponse>['winner'];
  };
};

const Monthly10kGiveaway = observer(
  ({ draw, lastMonthInfo }: Monthly10kGiveawayProps) => {
    const router = useRouter();
    const { isInMonthlyRaffle, initialized } = useUserStore();

    useEffect(() => {
      // Redirect to 404 if user is not in the monthly raffle
      // isInMonthlyRaffle will be false for non-logged in users
      if (router.isReady && initialized && !isInMonthlyRaffle) {
        router.replace('/404');
      }
    }, [router.isReady, initialized, isInMonthlyRaffle, router]);

    const nextDraw = draw.next_draw;

    if (!initialized || !nextDraw || !nextDraw.prize || !isInMonthlyRaffle) {
      return null;
    }

    const currentPrize = nextDraw.prize;
    const currentPrizeImages = Object.values(currentPrize.imageLocations)
      .filter((image): image is string => typeof image === 'string')
      .map((image) => ({
        id: image,
        image: (
          <div className={styles.carouselImageContainer}>
            <img src={image} alt={currentPrize.title} />
          </div>
        ),
      }));

    const lastMonthWinnerName = lastMonthInfo?.winner?.firstName;

    const lastMonthPrize = lastMonthInfo?.prize ? lastMonthInfo.prize : null;
    const lastMonthPrizeImages = lastMonthPrize
      ? Object.values(lastMonthPrize.imageLocations).filter(
          (image): image is string => typeof image === 'string',
        )
      : [];

    return (
      <MainLayout limitItemWidths>
        <NextSeo
          title="Monthly 10k Giveaway | Win Epic Prizes | DAYMADE"
          description="Enter our exclusive Monthly 10k Giveaway! Premium & Lite subscribers automatically receive weekly entries for a chance to win amazing prizes. One guaranteed winner every month."
          openGraph={{
            title: 'Monthly 10k Giveaway | Win Epic Prizes | DAYMADE',
            description:
              'Enter our exclusive Monthly 10k Giveaway! Premium & Lite subscribers automatically receive weekly entries for a chance to win amazing prizes. One guaranteed winner every month.',
          }}
        />
        <div className="aboutUsTypographyReset">
          {/* Banner */}
          <div className={styles.bannerContainer}>
            <div
              className={styles.banner}
              style={{
                ['--monthly-10k-giveaway-banner-filename-mobile' as any]: `url(${imageLocation}/mobile/Hero.svg)`,
                ['--monthly-10k-giveaway-banner-filename-desktop' as any]: `url(${imageLocation}/desktop/Hero.svg)`,
              }}
            >
              <div className={`${styles.bannerBadge}`}>
                <span>Bonus Raffle</span>
              </div>
              <Monthly10kGiveawayIcon className={styles.bannerIcon} />
            </div>
          </div>
          <AboutUsModule background="white" withReducedPadding>
            <div className={`${styles.container}`}>
              <AboutUsSiteContainer>
                <div className={styles.prizeInfoSection}>
                  {/* Prize Info Section */}
                  <h3 className="aboutUsTypographyHeaderMedium">
                    {getMonthName(draw.next_draw?.drawDate)} Prize
                  </h3>
                  <h2
                    data-text={currentPrize.title}
                    className="aboutUsTypographyHeaderXlargeOnWhite noMargin"
                  >
                    {currentPrize.title}
                  </h2>
                </div>
              </AboutUsSiteContainer>
              {/* --- images ---}*/}
              <div className={styles.carouselWrapper}>
                {/* Mobile/Tablet: Peek carousel */}
                <div className={styles.mobileOnly}>
                  <PeekCarousel items={currentPrizeImages} />
                </div>
                {/* Desktop: Gallery with thumbnails */}

                <div className={styles.desktopOnly}>
                  <div className={styles.bannerContainer}>
                    <DesktopPrizeGallery items={currentPrizeImages} />
                  </div>
                </div>
              </div>
              <AboutUsSiteContainer>
                <div className={styles.infoContainer}>
                  <div className={styles.infoTextContainer}>
                    <ReadMoreSection
                      introLines={[
                        'Welcome to our Monthly £10K Giveaway, with one guaranteed winner every month!',
                        'All DAYMADE subscribers automatically get entered into this monthly giveaway. All you have to do is be an active subscriber on the £10K Giveaway draw day (which always falls on the last day of the month) and keep your fingers and toes crossed!',
                        'This month, the £10k prize is ' +
                          toMidSentenceCase(currentPrize.title),
                      ]}
                      content={currentPrize.overviewBody ?? ''}
                    />
                  </div>
                  <div>
                    {parsePrizeHtmlSections({
                      whatsIncludedBody: currentPrize.whatsIncludedBody ?? '',
                      whatsIncludedHeading:
                        currentPrize.whatsIncludedHeading ?? '',
                    }).sections.map((x) => (
                      <FaqCard
                        questionElement="h2"
                        rebrand2025
                        withBorder
                        withInheritedAnswerTextColor
                        icon={getSectionHeaderIcon(x.sectionHeader)}
                        question={x.sectionHeader}
                        answer={
                          <>
                            <div
                              dangerouslySetInnerHTML={{
                                __html: x.content.reduce((acc, x) => {
                                  return acc + x.content;
                                }, ''),
                              }}
                            />
                            {x.isDefaultSection
                              ? renderBundleItems({
                                  type: currentPrize.type,
                                })
                              : undefined}
                          </>
                        }
                      />
                    ))}
                    <div className={styles.progressionBarContainer}>
                      <CurrentUserProgressionBar />
                    </div>
                  </div>
                </div>
              </AboutUsSiteContainer>
            </div>
          </AboutUsModule>
          {/* Good to Know Section */}
          <AboutUsModule withNoPadding background={'tealSolid'}>
            <AboutUsSiteContainer>
              <div
                className={`${styles.container} ${styles.goodToKnowContainer}`}
              >
                <h2
                  className={`${styles.mobileOnly} aboutUsTypographyHeaderXlargeOnWhite noMargin`}
                  data-text={'Good to know'}
                >
                  Good to know
                </h2>

                <div className={styles.goodToKnowCardsContainer}>
                  {getGoodToKnowCards(draw.next_draw?.drawDate).map(
                    (card, index) => (
                      <div key={card.title} className={styles.goodToKnowCard}>
                        <div className={styles.goodToKnowCardHeader}>
                          <img
                            src={`${imageLocation}/GoodToKnowHeader${index + 1}.svg`}
                            alt=""
                            className={styles.goodToKnowCardHeaderImage}
                          />
                        </div>
                        <div className={styles.goodToKnowCardBody}>
                          <div className={styles.goodToKnowCardTitleContainer}>
                            <div
                              className={`${styles.goodToKnowIcon} ${card.title === 'Weekly Entry' ? styles.rotateIcon : ''}`}
                            >
                              <span
                                className={styles.svgStyle}
                                aria-hidden="true"
                              >
                                {card.iconSvg}
                              </span>
                            </div>
                            <h3
                              className="aboutUsTypographyHeaderMedium"
                              style={{ marginTop: 0 }}
                            >
                              {card.mobileTitle && (
                                <span className={styles.mobileOnly}>
                                  {card.mobileTitle}
                                </span>
                              )}
                              <span
                                className={
                                  card.mobileTitle
                                    ? styles.desktopOnly
                                    : undefined
                                }
                              >
                                {card.title}
                              </span>
                            </h3>
                          </div>
                          <p
                            className={`${styles.goodToKnowBody} aboutUsTypographyBodySmall`}
                          >
                            {card.body}
                          </p>
                        </div>
                      </div>
                    ),
                  )}
                </div>
                <div className={styles.eligibilityMessageContainer}>
                  <p className={`${styles.noMargin} ${styles.textAlignCenter}`}>
                    The <strong>guaranteed winner</strong> will be emailed once
                    the draw closes.
                    <br className={styles.desktopOnly} />{' '}
                    <strong>
                      You must be an active subscriber to be eligible.
                    </strong>
                  </p>
                </div>
              </div>
            </AboutUsSiteContainer>
          </AboutUsModule>
          {/* Last Month's Winner Section */}
          {lastMonthInfo?.winner && lastMonthInfo?.prize && (
            <AboutUsModule withReducedPadding background={'white'}>
              <AboutUsSiteContainer>
                <div className={styles.container}>
                  <h2
                    className={`${styles.mobileOnly} aboutUsTypographyHeaderXlargeOnWhite noMargin`}
                    data-text={LAST_MONTH_WINNER_SECTION_TITLE}
                  >
                    {convertNewLinesToBr(LAST_MONTH_WINNER_SECTION_TITLE)}
                  </h2>

                  <div className={styles.lastWinnerContainer}>
                    <div className={styles.lastWinnerImageContainer}>
                      <img
                        src={lastMonthPrizeImages[0]}
                        alt={`${lastMonthWinnerName} - last month's winner`}
                      />
                      <div
                        className={`${styles.lastWinnerOverlay} ${styles.mobileOnly}`}
                      >
                        <p
                          className={`aboutUsTypographyHeaderMedium ${styles.lastWinnerName}`}
                        >
                          {lastMonthWinnerName}
                        </p>
                        {lastMonthPrize && (
                          <p className="noMargin">
                            {lastMonthPrize.title} winner
                          </p>
                        )}
                      </div>
                    </div>
                    <div
                      className={`${styles.lastWinnerDetails} ${styles.desktopOnly}`}
                    >
                      <h2
                        className={`aboutUsTypographyHeaderXlargeOnWhite noMargin ${styles.textAlignLeft}`}
                        data-text={LAST_MONTH_WINNER_SECTION_TITLE}
                      >
                        {convertNewLinesToBr(LAST_MONTH_WINNER_SECTION_TITLE)}
                      </h2>
                      <p className={`aboutUsTypographyHeaderMedium`}>
                        {lastMonthWinnerName}
                      </p>
                      {lastMonthPrize && (
                        <p className="noMargin">
                          {lastMonthPrize.title} winner
                        </p>
                      )}
                    </div>
                  </div>
                </div>
              </AboutUsSiteContainer>
            </AboutUsModule>
          )}
        </div>
      </MainLayout>
    );
  },
);

export const getServerSideProps: GetServerSideProps<
  Monthly10kGiveawayProps
> = async () => {
  const lastRafflePrizeAndWinnerResult =
    await drawApiClient.public.getLastRafflePrizeAndWinner({
      params: { draw_schedule_code: Monthly10k.drawTypeCode },
    });

  const drawRequest = await drawApiClient.public.getDrawTypeCached({
    params: { draw_code: Monthly10k.drawTypeCode },
  });

  if (drawRequest.status === HttpStatusCode.NOT_FOUND) {
    return {
      notFound: true,
    };
  }

  if (!drawRequest.body.next_draw || !drawRequest.body.next_draw.prize) {
    return {
      notFound: true,
    };
  }

  const drawRequestParsed: RoundTripSerialized<typeof drawRequest> = JSON.parse(
    JSON.stringify(drawRequest),
  );

  let lastRafflePrize = null;
  let lastRaffleWinner = null;

  if (lastRafflePrizeAndWinnerResult.status === HttpStatusCode.OK) {
    lastRafflePrize = lastRafflePrizeAndWinnerResult.body.prize;
    lastRaffleWinner = lastRafflePrizeAndWinnerResult.body.winner;
  }

  return {
    props: {
      draw: drawRequestParsed.body,
      lastMonthInfo: {
        prize: lastRafflePrize,
        winner: lastRaffleWinner,
      },
    },
  };
};

export default Monthly10kGiveaway;
